<template>
    <div class="d-flex flex-wrap flex-stack my-5">
        <h2 class="fs-2 fw-bold my-2">
            <template v-if="dataID">
                {{ sprintf($t('pages.module.studentSuccess.save.edit'), [(form.data.firstname + ' ' + form.data.lastname) ?? '']) }}
            </template>
            <template v-else>{{ $t('pages.module.studentSuccess.save.new') }}</template>
        </h2>

        <router-link to="/module/student-success" class="btn btn-primary align-self-center">
            <span class="svg-icon svg-icon-2">
                <inline-svg src="/media/icons/duotune/arrows/arr021.svg"/>
            </span>
            {{ $t("pages.module.studentSuccess.title") }}
        </router-link>
    </div>
    <el-form :model="form" ref="studentSuccessForm">
        <div class="row g-6 mb-6 g-xl-9 mb-xl-9">
            <div class="col-md-8">
                <div class="card">
                    <div class="card-body d-flex flex-column p-9">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="fv-row mb-1">
                                    <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.save.cols.firstname') }}</label>
                                    <el-form-item prop="data.firstname" :rules="$validation.getMessage(['required'])">
                                        <el-input v-model="form.data.firstname" type="text"/>
                                    </el-form-item>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="fv-row mb-1">
                                    <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.save.cols.lastname') }}</label>
                                    <el-form-item prop="data.lastname" :rules="$validation.getMessage(['required'])">
                                        <el-input v-model="form.data.lastname" type="text"/>
                                    </el-form-item>
                                </div>
                            </div>
                        </div>
                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.save.cols.department') }}</label>
                            <el-form-item prop="data.options.areas.department">
                                <el-input v-model="form.data.options.areas.department" type="text"/>
                            </el-form-item>
                        </div>
                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.save.cols.university') }}</label>
                            <el-form-item prop="data.options.areas.university">
                                <el-input v-model="form.data.options.areas.university" type="text"/>
                            </el-form-item>
                        </div>
                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.save.cols.exam_name') }}</label>
                            <el-form-item prop="data.options.areas.exam_name">
                                <el-input v-model="form.data.options.areas.exam_name" type="text"/>
                            </el-form-item>
                        </div>
                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.save.cols.ranked') }}</label>
                            <el-form-item prop="data.options.areas.ranked">
                                <el-input v-model="form.data.options.areas.ranked" type="text"/>
                            </el-form-item>
                        </div>
                        <div class="fv-row mb-1">
                            <el-alert :title="$t('pages.module.studentSuccess.save.cols.themeVisibilityNotice')" type="warning" :closable="false" />
                        </div>
                    </div>
                </div>

                <div class="card mt-7">
                    <div class="card-body d-flex flex-column p-9">

                        <div class="fv-row mb-7">
                            <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.cols.streamData.api') }}</label>
                            <el-form-item prop="data.stream_data.api" :rules="$validation.getMessage(['required'])">
                                <el-select v-model="form.data.stream_data.api" class="w-100" :placeholder="$t('common.chooseSelect')" @change="selectedStreamApi">
                                    <el-option v-for="(provider, providerIndex) in streamProviders" :key="providerIndex" :value="(provider.code.toUpperCase())" :label="provider.name"></el-option>
                                </el-select>
                            </el-form-item>
                        </div>

                        <template v-if="['VIMEO', 'VISTREAM', 'YOUTUBE'].includes(form.data.stream_data.api)">
                            <div class="fv-row mb-7" v-if="form.data.stream_data.provider_id">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.cols.streamData.accountId') }}</label>
                                <el-form-item prop="data.stream_data.account_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.stream_data.account_id" class="w-100" :placeholder="$t('common.chooseSelect')">
                                        <el-option v-for="(account, accountIndex) in provider.accounts" :key="accountIndex" :value="account.id" :label="account.title"></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.cols.streamData.videoId') }}</label>
                                <el-form-item prop="data.stream_data.video_id" :rules="$validation.getMessage(['required'])">
                                    <el-input v-model="form.data.stream_data.video_id" type="text"/>
                                </el-form-item>
                            </div>
                        </template>

                        <template v-if="['LOCAL'].includes(form.data.stream_data.api)">
                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.studentSuccess.cols.streamData.video') }}</label>
                                <el-form-item prop="data.stream_data.video_id" :rules="$validation.getMessage(['required'])">
                                    <el-upload :accept="$root.acceptFileSupported('video', '.')" v-bind:class="{'disabled-upload': video.fileList.length > 0}" :on-change="handleChangeVideo" :on-remove="handleChangeVideo"
                                               list-type="picture-card" :file-list="video.fileList" limit="1" :on-preview="handleVideoCardPreview" :auto-upload="false">
                                        <i class="bi bi-plus"/>
                                    </el-upload>
                                    <el-dialog v-model="video.dialogVisible" width="30%">
                                        <video style="width: 100%" :src="video.dialogFileUrl" controls/>
                                    </el-dialog>
                                </el-form-item>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div class="col-md-4">
                <div class="card">
                    <div class="card-body d-flex flex-column p-9">
                        <div class="fv-row mb-7">
                            <label class="fs-6 fw-bold mb-2" :class="{'required': ['LOCAL'].includes(form.data.stream_data.api)}">{{ $t('pages.module.studentSuccess.cols.image') }}</label>
                            <el-form-item prop="data.image_id" v-if="['LOCAL'].includes(form.data.stream_data.api)" :rules="$validation.getMessage(['required'])">
                                <el-upload :accept="$root.acceptFileSupported('image', 'image/')" v-bind:class="{'disabled-upload': image.fileList.length > 0}" :on-change="handleChangeImage" :on-remove="handleChangeImage"
                                           list-type="picture-card" :file-list="image.fileList" limit="1" :on-preview="handlePictureCardPreview" :auto-upload="false">
                                    <i class="bi bi-plus"/>
                                </el-upload>
                                <el-dialog v-model="image.dialogVisible" width="30%">
                                    <img style="width: 100%" :src="image.dialogImageUrl" alt=""/>
                                </el-dialog>
                            </el-form-item>
                            <el-form-item prop="data.image_id" v-else>
                                <el-upload :accept="$root.acceptFileSupported('image', 'image/')" v-bind:class="{'disabled-upload': image.fileList.length > 0}" :on-change="handleChangeImage" :on-remove="handleChangeImage"
                                           list-type="picture-card" :file-list="image.fileList" limit="1" :on-preview="handlePictureCardPreview" :auto-upload="false">
                                    <i class="bi bi-plus"/>
                                </el-upload>
                                <el-dialog v-model="image.dialogVisible" width="30%">
                                    <img style="width: 100%" :src="image.dialogImageUrl" alt=""/>
                                </el-dialog>
                            </el-form-item>
                        </div>

                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('common.sort') }}</label>
                            <el-form-item prop="data.sort" :rules="$validation.getMessage(['required'])">
                                <el-input-number v-model="form.data.sort" :min="1"/>
                            </el-form-item>
                        </div>

                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('common.status') }}</label>
                            <el-form-item prop="data.active">
                                <el-radio-group v-model="form.data.active">
                                    <el-radio-button :label="true">{{ $t("common.active") }}</el-radio-button>
                                    <el-radio-button :label="false">{{ $t("common.passive") }}</el-radio-button>
                                </el-radio-group>
                            </el-form-item>
                        </div>
                        <div class="fv-row mt-5">
                            <el-form-item class="mb-0">
                                <button @click.prevent="onSubmit" :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary w-100" type="button">
                                    <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                    <span v-if="form.loading" class="indicator-progress">
                                        {{ $t("messages.wait") }}
                                        <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </el-form-item>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </el-form>
</template>

<script>
import CustomCkeditor from "@/components/custom-ckeditor"

export default {
    name: "_id",
    components: {
        CustomCkeditor
    },
    data() {
        return {
            form: {
                updateStatus: false,
                loading: false,
                translate: {
                    language_id: this.$root.defaultLanguage.id,
                    meta: {}
                },
                data: {
                    stream_data: {
                        api: '',
                        provider_id: '',
                        account_id: '',
                        video_id: '',
                        duration: '',
                        thumb_img: {
                            base: ''
                        },
                    },
                    options: {
                        areas: {
                            university: "",
                            department: "",
                            exam_name: "",
                            ranked: "",
                        }
                    },
                    sort: 1,
                    active: true
                }
            },
            image: {
                dialogImageUrl: '',
                dialogVisible: false,
                uploadList: [],
                fileList: []
            },
            video: {
                dialogFileUrl: '',
                dialogVisible: false,
                uploadList: [],
                fileList: []
            },
            provider: {
                accounts: []
            }
        }
    },
    computed: {
        dataID() {
            return this.$route.params.id;
        },
        streamProviders() {
            return this.$store.state.stream.provider.table.data;
        },
        streamProvidersObj() {
            let obj = {};

            this.streamProviders.forEach((provider) => {
                obj[provider.code.toUpperCase()] = provider;
            });

            return obj;
        }
    },
    created() {
        if (this.dataID && !(this.dataID > 0)) {
            this.$router.push({
                path: "/module/student-success"
            });
        }
    },
    mounted() {
        this.$store.dispatch('stream/provider/get', {
            page: {pageSize: 9999},
            filterData: {
                sort: 'created_at:desc',
                platform: ['public']
            }
        }).then((response) => {
            this.streamProviders.push({
                code: "Local",
                id: 0,
                name: "Yerel Hafıza"
            })
        });

        if (this.dataID && this.dataID > 0) {
            this.loadData();
        }
    },
    methods: {
        loadData(dataID = this.dataID) {
            this.resetImageData();

            this.axios.get(this.endpoints['module_student_success'] + '/' + dataID).then((response) => {
                let data = response.data.data;

                let translateLinks = this.resolveDatum(data.links, this.$root.defaultLanguage.id, 'language_id');
                data.translate = this.resolveDatum(data.translations, this.$root.defaultLanguage.id, 'language_id');

                this.blog = data;

                this.form.updateStatus = true;
                this.form.data = data;

                if (this.form.data.stream_data?.provider_id) {
                    this.loadStreamProviderAccounts(this.form.data.stream_data.provider_id);
                }

                if (data.image) {
                    this.image.fileList = [
                        {
                            name: data.image.name,
                            url: data.image.public_url
                        }
                    ]
                }
                if (data.stream_data.file) {
                    this.video.fileList = [
                        {
                            name: data.stream_data.file.name,
                            url: '/media/svg/files/video.svg',
                            videoUrl: data.stream_data.file.public_url
                        }
                    ]
                }
            })
        },
        onSubmit() {
            this.form.loading = true;
            this.form.data.stream_data.file = null;

            if (this.image.uploadList.length > 0 || this.video.uploadList.length > 0) {
                this.submitFormWithUpload();
            } else {
                if (!(this.image.fileList.length > 0)) {
                    this.form.data.image_id = null;
                }
                this.submitForm();
            }
        },
        submitFormWithUpload() {
            const formData = new FormData();

            this.image.uploadList.forEach(file => {
                formData.append("file[]", file.raw);
            });
            this.video.uploadList.forEach(file => {
                formData.append("file[]", file.raw);
            });

            this.axios.post(this.endpoints["file"], formData).then(response => {
                this.onResponse(response.data, () => {
                    let data = response.data.data;

                    data.forEach((item) => {
                        if (item.extension_detail.group === 'video') {
                            this.form.data.stream_data = {
                                api: 'LOCAL',
                                video_id: item.id
                            };
                            this.video.uploadList = [];
                        }
                        if (item.extension_detail.group === 'image') {
                            this.form.data.image_id = item.id;
                            this.image.uploadList = [];
                        }
                    })

                    this.submitForm();
                }, () => {
                    this.form.loading = false;
                }, false);
            }).catch(error => {
                this.onResponseFailure(error.response.data, () => {
                    this.form.loading = false;
                });
            });
        },
        submitForm() {
            this.$refs.studentSuccessForm.validate((valid) => {
                if (valid) {
                    let formData = this.prepareFormData();

                    this.form.loading = true;

                    if (this.form.data.id) {
                        this.axios.put(this.endpoints['module_student_success'] + '/' + this.form.data.id, formData).then(response => {
                            this.onResponse(response.data, () => {
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    } else {
                        this.axios.post(this.endpoints['module_student_success'], formData).then(response => {
                            this.onResponse(response.data, () => {
                                this.$router.push({
                                    path: "/module/student-success/save/" + response.data.data.id
                                });
                                this.loadData(response.data.data.id);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }
                } else {
                    return false;
                }
            });

        },
        prepareFormData() {
            let formData = this.form.data;

            return {...this.form.translate, ...formData}
        },
        resetImageData() {
            this.image = {
                dialogImageUrl: '',
                dialogVisible: false,
                uploadList: [],
                fileList: []
            }
        },
        handlePictureCardPreview(file) {
            this.image.dialogImageUrl = file.url
            this.image.dialogVisible = true
        },
        handleChangeImage(file, fileList) {
            this.image.fileList = fileList;
            this.image.uploadList = fileList;
        },

        resetVideoData() {
            this.video = {
                dialogVideoUrl: '',
                dialogVisible: false,
                uploadList: [],
                fileList: []
            }
        },
        handleVideoCardPreview(file) {
            this.video.dialogFileUrl = file.videoUrl
            this.video.dialogVisible = true
        },
        handleChangeVideo(file, fileList) {
            this.video.fileList = fileList;
            this.video.uploadList = fileList;
            const videoUrl = this.video.fileList[0].url;
            this.video.fileList[0].url = '/media/svg/files/video.svg';
            this.video.fileList[0].videoUrl = videoUrl;
        },

        selectedStreamApi() {
            let formData = this.form.data;
            let providerData = {
                api: formData.stream_data.api
            };

            if (this.streamProvidersObj[formData.stream_data.api]) {
                providerData.provider_id = this.streamProvidersObj[formData.stream_data.api].id;
                this.loadStreamProviderAccounts(providerData.provider_id);
            }

            this.form.data.stream_data = providerData;
        },
        loadStreamProviderAccounts(providerId) {
            if (providerId === 0) {
                return false
            }
            this.axios.get(this.endpoints['stream_provider_account'], {
                params: {
                    provider_id: providerId,
                    sort: "created_at:desc"
                }
            }).then(response => {
                let data = response.data.data;
                this.provider.accounts = data;
            });
        }
    }
}
</script>

<style>
</style>